import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "body", "input"]

  connect() {
    this._handleKeydown = (e) => {
      if (e.key === "Escape") this.close()
    }
  }

  open(event) {
    event.preventDefault()

    // If guest-modal controller exists on this element, delegate to it
    const guestModal = this.element.querySelector("[data-guest-modal-target='modal']")
    if (guestModal) {
      guestModal.classList.add("guest-modal--open")
      document.body.style.overflow = "hidden"
      return
    }

    this.modalTarget.classList.add("comment-modal--open")
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this._handleKeydown)

    // Find the feed video for this post and pause it
    const post = this.element.closest("[id^='post_']")
    this._feedVideo = post ? post.querySelector("video") : null
    if (!this._feedVideo) {
      document.querySelectorAll("video").forEach(v => { if (!v.paused) { this._feedVideo = v; } })
    }
    if (this._feedVideo && !this._feedVideo.paused) {
      this._feedVideoTime = this._feedVideo.currentTime
      this._feedVideo.pause()
    }

    // Play modal video synced to feed video time, with sound
    const modalVideo = this.modalTarget.querySelector("video")
    if (modalVideo) {
      if (this._feedVideo) {
        modalVideo.currentTime = this._feedVideoTime || 0
      }
      // Listen for play event to unmute
      const unmute = () => {
        modalVideo.muted = false
        modalVideo.volume = 1
        modalVideo.removeEventListener("playing", unmute)
      }
      modalVideo.addEventListener("playing", unmute)
      // Start muted (browser allows), then unmute immediately
      modalVideo.muted = true
      modalVideo.play().then(() => {
        modalVideo.muted = false
        modalVideo.volume = 1
      }).catch(() => {})
    }

    // Scroll to bottom of comments
    setTimeout(() => {
      if (this.hasBodyTarget) {
        this.bodyTarget.scrollTop = this.bodyTarget.scrollHeight
      }
    }, 100)
  }

  close() {
    this.modalTarget.classList.remove("comment-modal--open")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._handleKeydown)

    // Pause modal video and sync time back to feed
    const modalVideo = this.modalTarget.querySelector("video")
    if (modalVideo) {
      const modalTime = modalVideo.currentTime
      modalVideo.pause()

      // Resume feed video from modal's current time
      if (this._feedVideo) {
        this._feedVideo.currentTime = modalTime
        this._feedVideo.play().catch(() => {})
      }
    } else if (this._feedVideo) {
      this._feedVideo.play().catch(() => {})
    }
    this._feedVideo = null
    this._feedVideoTime = null

    this.cancelReply()
  }

  backdropClick(event) {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }

  focusInput() {
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
  }

  startReply(event) {
    const username = event.currentTarget.dataset.replyUsername
    const commentId = event.currentTarget.dataset.replyCommentId

    // Set hidden parent_id field
    const parentField = this.modalTarget.querySelector("[id$='_reply_parent_id']")
    if (parentField) parentField.value = commentId

    // Show reply indicator
    const indicator = this.modalTarget.querySelector("[id$='_reply_indicator']")
    if (indicator) {
      indicator.style.display = "flex"
      indicator.querySelector(".comment-modal__reply-indicator-text").textContent =
        `Respondendo a @${username}`
    }

    // Focus input (don't prefill @username — the rendered reply will show the mention)
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
  }

  cancelReply() {
    const parentField = this.modalTarget?.querySelector("[id$='_reply_parent_id']")
    if (parentField) parentField.value = ""

    const indicator = this.modalTarget?.querySelector("[id$='_reply_indicator']")
    if (indicator) indicator.style.display = "none"

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
  }

  onSubmitEnd(event) {
    if (event.detail.success) {
      // Clear the input and reply state
      if (this.hasInputTarget) this.inputTarget.value = ""
      this.cancelReply()

      // Close emoji picker if open
      const emojiPicker = this.element.querySelector(".emoji-picker--open")
      if (emojiPicker) emojiPicker.classList.remove("emoji-picker--open")

      // Scroll to bottom to show new comment
      setTimeout(() => {
        if (this.hasBodyTarget) {
          this.bodyTarget.scrollTop = this.bodyTarget.scrollHeight
        }
      }, 100)
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleKeydown)
    document.body.style.overflow = ""
  }
}
